import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, finalize, switchMap, take, tap } from 'rxjs/operators';
import { OrgDetail } from '../../../models/organization-detail.model';
import { ProductDetail } from '../../../models/product-detail';
import { NotificationService } from '../../../services';
import { AppFacadeService } from '../../../services/app-facade.service';
import { ContractService } from '../../../services/contract.service';
import { OrganizationIdService } from '../../../services/organization-id.service';
import { loadProductFail, loadProductSuccess, loadPublicProductByAssetTag, loadPublicProductByAssetTagFail, ProductActionTypes } from './product.actions';

@Injectable()
export class ProductEffects {
    private readonly actions$ = inject(Actions);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly contractService = inject(ContractService);
    private readonly notification = inject(NotificationService);
    private readonly translateService = inject(TranslateService);
    private readonly organizationIdService = inject(OrganizationIdService);

    fetchProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductActionTypes.LoadProduct),
            tap(() => this.appFacadeService.setLoading(true)),
            switchMap(action => {
                const { customerId, columns, productId, stockId, callback }: { customerId: string; columns: string[]; productId: string; stockId: string; callback?: () => void } = action;

                return this.contractService.getProductDetails(customerId, productId, stockId, columns).pipe(
                    switchMap((data: ProductDetail[]) => {
                        if (callback) {
                            callback();
                        }
                        return of(loadProductSuccess({ productDetail: data }));
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.notification.error(this.translateService.instant('notFoundProductDetail'));
                        return of(loadProductFail({ error }));
                    }),
                    finalize(() => {
                        this.appFacadeService.setLoading(false);
                    })
                );
            })
        )
    );

    /**
     * Effect for public asset tag product lookup.
     *
     * Flow:
     * 1. Resolve orgId from current URL via public/organizations/url API
     * 2. Use resolved orgId to call getPublicProductByAssetTag
     * 3. On success: dispatch loadProductSuccess (reducer sets loading=false, error=null)
     * 4. On org resolution failure: dispatch loadPublicProductByAssetTagFail with OrgNotFound token
     * 5. On product fetch failure: dispatch loadPublicProductByAssetTagFail with typed error token
     *
     * No callback pattern — state transitions are handled by reducers + selectors.
     * No toast notification — the component error page handles error display.
     */
    fetchPublicProductByAssetTag$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadPublicProductByAssetTag),
            tap(() => this.appFacadeService.setLoading(true)),
            switchMap(({ assetTagId }) =>
                this.appFacadeService.getConfig().pipe(
                    take(1),
                    switchMap(config => {
                        const orgLookupUrl = window.location.href.includes('localhost') ? config?.defaultSubDomain || window.location.origin : window.location.origin;

                        return this.organizationIdService.getOrgIdFromUrl(orgLookupUrl).pipe(
                            switchMap(orgResult => {
                                if (!orgResult) {
                                    this.appFacadeService.setLoading(false);
                                    return of(
                                        loadPublicProductByAssetTagFail({
                                            error: { token: 'OrgNotFound', message: 'Could not resolve organization from URL' }
                                        })
                                    );
                                }

                                const orgDetail = orgResult as unknown as OrgDetail;
                                const orgId = orgDetail?.OrganizationId?.toString() || '';

                                return this.contractService.getPublicProductByAssetTag(orgId, assetTagId).pipe(
                                    switchMap((data: ProductDetail[]) => {
                                        const stockId = data?.[0]?.Id?.toString() || '';
                                        const orgType = orgDetail?.Type || '';
                                        return of(
                                            loadProductSuccess({
                                                productDetail: data,
                                                documentValue: stockId,
                                                customerId: '0',
                                                contractId: '',
                                                orgType,
                                                orgId
                                            })
                                        );
                                    }),
                                    catchError((error: HttpErrorResponse) => {
                                        const token = error.error?.token || 'ServiceUnavailable';
                                        return of(
                                            loadPublicProductByAssetTagFail({
                                                error: { token, message: error.error?.message }
                                            })
                                        );
                                    }),
                                    finalize(() => {
                                        this.appFacadeService.setLoading(false);
                                    })
                                );
                            }),
                            catchError(() => {
                                this.appFacadeService.setLoading(false);
                                return of(
                                    loadPublicProductByAssetTagFail({
                                        error: { token: 'OrgNotFound', message: 'Org resolution failed with exception' }
                                    })
                                );
                            })
                        );
                    })
                )
            )
        )
    );
}
